import type { SessionRecordingSummary, VideoFile } from "@/types";

export interface RecordingVideoFileGroup {
  recording: SessionRecordingSummary;
  index: number;
}

export interface GroupedVideoFile {
  video: VideoFile;
  recordingGroup: RecordingVideoFileGroup | null;
  startsRecordingGroup: boolean;
}

export const groupVideoFilesByRecording = (
  videos: VideoFile[],
  recordings?: SessionRecordingSummary[],
): GroupedVideoFile[] => {
  if (!recordings) {
    return videos.map((video) => ({
      video,
      recordingGroup: null,
      startsRecordingGroup: false,
    }));
  }

  const groupByVideoId = new Map<string, RecordingVideoFileGroup>();
  const orderByVideoId = new Map<string, number>();
  let nextOrder = 0;

  recordings.forEach((recording, index) => {
    recording.chapters.forEach((chapter) => {
      groupByVideoId.set(chapter.id, { recording, index });
      orderByVideoId.set(chapter.id, nextOrder);
      nextOrder += 1;
    });
  });

  const orderedVideos = videos
    .map((video, originalIndex) => ({ video, originalIndex }))
    .sort((left, right) => {
      const leftOrder = orderByVideoId.get(left.video.id);
      const rightOrder = orderByVideoId.get(right.video.id);
      if (leftOrder !== undefined && rightOrder !== undefined) {
        return leftOrder - rightOrder;
      }
      if (leftOrder !== undefined) return -1;
      if (rightOrder !== undefined) return 1;
      return left.originalIndex - right.originalIndex;
    })
    .map(({ video }) => video);

  let previousGroupId: string | null = null;
  return orderedVideos.map((video) => {
    const recordingGroup = groupByVideoId.get(video.id) ?? null;
    const groupId = recordingGroup?.recording.id ?? "unassigned";
    const startsRecordingGroup = groupId !== previousGroupId;
    previousGroupId = groupId;

    return { video, recordingGroup, startsRecordingGroup };
  });
};
